/**
 * AppRoutes.company.jsx — 회사별(ext) 라우트 래퍼 (2026-09-10 분리)
 *
 * AppRoutes.jsx 가 464줄이라 300줄 규칙에 걸려 분리했다. **코드 이동만이고 동작 변경은 없다.**
 *
 * 여기 있는 것들은 전부 같은 일을 한다 — URL 의 :code 를 대문자 회사코드로 바꿔
 * 실제 화면 컴포넌트에 companyCode 로 넘긴다. 화면은 회사마다 같고 데이터만 다르다.
 */
import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { COMPANY_MENU_FEATURES, hqRedirectPath, hqCodeOrDefault } from './pages/admin/adminlayout/companyFeatures';
import { useCompanyScope } from './pages/admin/adminlayout/CompanyScopeContext';
import { callAllRouteDecision } from './pages/admin/adminlayout/callMenuNaming';
import AdminDotRevenue from './pages/admin/AdminDotRevenue';
import AdminSettlementManagement from './pages/admin/AdminSettlementManagement';
import AdminSettlementDotManagement from './pages/admin/AdminSettlementDotManagement';
import ContractCancelManagement from './pages/admin/ContractCancelManagement';
import AnnualLeaveSettlement from './pages/admin/leavesettlement/AnnualLeaveSettlement';
import ScoreManagement from './pages/admin/scoremgmt/ScoreManagement';
import ContractRenewalManagement from './pages/admin/contractrenewal/ContractRenewalManagement';
import AdminMarketerConsultantPage from './pages/admin/AdminMarketerConsultantPage';
import CallRegister from './pages/tm/CallRegister';
import PayoutManagement from './pages/admin/PayoutManagement';
import ExternalCompanyUsers from './pages/admin/ExternalCompanyUsers';
import ExternalCompanyLeaves from './pages/admin/ExternalCompanyLeaves';
// 회사별 출퇴근 관리 = [달력][직원별 상세] 탭 (2026-09-29, 옛 ExternalCompanyAttendance + …Calendar 합침)
import { ExtAttendanceHub } from './pages/admin/attendancehub/AttendanceHubRoutes';
import ExternalCompanyIncentive from './pages/admin/ExternalCompanyIncentive';
import ExternalCompanyTmCalls from './pages/admin/ExternalCompanyTmCalls';
import ExternalCompanyPayroll from './pages/admin/ExternalCompanyPayroll';
import TerminalManagement from './pages/admin/TerminalManagement';
import ContractManagement from './pages/admin/ContractManagement';
import ConfidentialLockGate from './pages/admin/confidential/ConfidentialLockGate';
import { scopedContractApi } from './pages/admin/contractmanagement/contractApi';
import PromotionalManagement from './pages/admin/PromotionalManagement';
import { scopedPromoApi } from './pages/admin/promotionalmanagement/promoApi';
import AdminMeetingMap from './pages/admin/AdminMeetingMap';
import { meetingMapApi } from './pages/tm/meetingMapApi';

// 회사별 매출 관리 라우트: companyCode를 AdminDotRevenue에 전달 (DOT는 기존 API, 그 외는 범용 ext API)
export function ExtRevenueRoute() {
  const { code } = useParams();
  return <AdminDotRevenue companyCode={(code || 'DOT').toUpperCase()} />;
}

// 회사별 정산서 관리 라우트: DOT는 전용 컴포넌트, 그 외는 AdminSettlementManagement 재사용 (companyCode prop 전달)
export function ExtSettlementRoute() {
  const { code } = useParams();
  if ((code || '').toUpperCase() === 'DOT') return <AdminSettlementDotManagement />;
  // DOT 외 모든 회사(LNJ 등): companyCode를 대문자로 전달해 마케터/정산 데이터 필터링
  return <AdminSettlementManagement companyCode={(code || '').toUpperCase()} />;
}

export function ExtContractCancelRoute() {
  const { code } = useParams();
  return <ContractCancelManagement companyCode={(code || 'DOT').toUpperCase()} />;
}

// 회사별 연차정산 라우트: companyCode 를 AnnualLeaveSettlement 에 전달 (PINE 베이스는 별도 고정 라우트)
export function ExtLeaveSettlementRoute() {
  const { code } = useParams();
  const c = (code || 'DOT').toUpperCase();
  // 대외비(2026-10-16) — 그 회사 대외비 비밀번호가 풀린 뒤에 화면이 열린다
  return (
    <ConfidentialLockGate key={c} companyCode={c} menuName="연차정산" menuKey="leave_settlement">
      <AnnualLeaveSettlement companyCode={c} />
    </ConfidentialLockGate>
  );
}

export function ExtScoreRoute() {
  const { code } = useParams();
  return <ScoreManagement companyCode={(code || 'DOT').toUpperCase()} />;
}

export function ExtContractRenewalRoute() {
  const { code } = useParams();
  const companyCode = (code || 'DOT').toUpperCase();
  // 제목에 회사코드를 붙이지 않는다 — 어느 회사인지는 사이드바 상단 선택창이 알려준다 (2026-09-10).
  return <ContractRenewalManagement companyCode={companyCode} pageTitle="계약갱신관리" />;
}

export function ExtMarketerConsultantRoute() {
  const { code } = useParams();
  const companyCode = (code || 'DOT').toUpperCase();
  return (
    <AdminMarketerConsultantPage
      fixedCompanyCode={companyCode}
      fixedCompanyName={companyCode}
      pageTitle="마케터 1:1 관리"
    />
  );
}

// /my/tm-calls-ext/:code — 회사별 동적 일일콜현황 (DOT/LNJ 외 신규 회사·테넌트용).
//   메뉴는 buildExtCallMenus(TM_FEATURE_EXT_{CODE})가 만든다. 배정 검증은 서버(call_company_code)가 수행.
export function MyExtCallRoute() {
  const { code } = useParams();
  const companyCode = String(code || '').toUpperCase();
  if (!/^[A-Z0-9_]{2,20}$/.test(companyCode)) return <Navigate to="/my" replace />;
  return <CallRegister adminMode={false} companyCode={companyCode} key={`my-tm-ext-${companyCode}`} />;
}

// 회사별 정산 관리 (2026-09-10). 정산 관리만 유일하게 ext 체계 밖(/admin/payout-management-{code})
//   이었고 라우트는 DOT·LNJ 두 개만 박혀 있어, 그 외 회사는 **메뉴는 뜨는데 눌러도 홈으로 튕겼다**.
//   실측: 미팅링크 유기자차(SUNCREAM), 파인그룹 TESTDOT·TESTPINE.
//   서버(/api/admin/payout/*)는 이미 회사코드를 가리지 않으므로(_auth.js:21 정규식) 주소만 있으면 된다.
//   옛 주소 두 개는 즐겨찾기용으로 AppRoutes 에 그대로 남겨 둔다.
export function ExtPayoutRoute() {
  const { code } = useParams();
  const companyCode = String(code || '').toUpperCase();
  if (!/^[A-Z0-9_]{2,20}$/.test(companyCode)) return <Navigate to="/admin" replace />;
  return <PayoutManagement companyCode={companyCode} />;
}

// 회사별 계약 관리 (2026-10-15, 4단계 계약 통합 4-5). 본사 계약 관리 화면 한 벌에 회사 주소 묶음만 넘긴다 —
//   그 회사 계약만 보이고, 콜 없이 등록하면 그 회사 계약이 된다(서버 ext-contracts.route.js). 홍보물은 보기만.
//   key: 같은 주소에서 회사만 바꿔 열면 화면을 새로 그린다(목록을 처음 한 번만 읽으므로).
export function ExtContractRoute() {
  const { code } = useParams();
  const companyCode = String(code || '').trim().toUpperCase();
  const api = useMemo(() => scopedContractApi(`/api/admin/ext/${companyCode}/contracts`), [companyCode]);
  if (!/^[A-Z0-9_]{2,20}$/.test(companyCode)) return <Navigate to="/admin" replace />;
  return <ContractManagement key={companyCode} api={api} />;
}

// 회사별 홍보물 관리(2026-10-19, 5단계 5-6) — 홍보물 관리 화면 한 벌에 /api/admin/ext/{회사}/promotional 주소 묶음
export function ExtPromotionalRoute() {
  const { code } = useParams();
  const companyCode = String(code || '').trim().toUpperCase();
  const api = useMemo(() => scopedPromoApi(`/api/admin/ext/${companyCode}/promotional`), [companyCode]);
  if (!/^[A-Z0-9_]{2,20}$/.test(companyCode)) return <Navigate to="/admin" replace />;
  return <PromotionalManagement key={companyCode} api={api} />;
}

// 회사별 미팅지도 관리(2026-10-22) — 미팅지도 관리 화면 한 벌에 /api/admin/ext/{회사}/meeting-map 주소 묶음.
//   설정(⚙️)은 테넌트 공용 — 이 회사 미팅지도 열쇠가 있으면 여기서도 바꾼다(사용자 결정).
export function ExtMeetingMapRoute() {
  const { code } = useParams();
  const companyCode = String(code || '').trim().toUpperCase();
  const api = useMemo(() => meetingMapApi(`/api/admin/ext/${companyCode}/meeting-map`), [companyCode]);
  const settingsKeys = useMemo(() => [`MEETING_MAP_${companyCode}_MANAGEMENT`, 'MEETING_MAP_MANAGEMENT'], [companyCode]);
  if (!/^[A-Z0-9_]{2,20}$/.test(companyCode)) return <Navigate to="/admin" replace />;
  return <AdminMeetingMap key={companyCode} api={api} settingsKeys={settingsKeys} subtitle="이 회사의 미팅 일정·결과를 지도로 관리합니다." />;
}

// ── 기능 key → 화면 (2026-09-11 C2) ──────────────────────────────────────────
//   주소는 companyFeatures.js 의 표가 정한다. 여기는 "그 주소로 가면 무엇이 뜨는가" 만 갖는다.
//   표에 줄을 넣고 여기를 빠뜨리면(또는 그 반대면) 아래 검사가 즉시 잡는다 —
//   전에는 그 어긋남이 조용히 남아 **메뉴는 뜨는데 눌러도 홈으로 튕겼다**(2026-09-10 정산 관리).
export const COMPANY_ELEMENTS = {
  users:           <ExternalCompanyUsers />,
  attendance:      <ExtAttendanceHub />,
  leave:           <ExternalCompanyLeaves />,
  leaveSettle:     <ExtLeaveSettlementRoute />,
  contractRenewal: <ExtContractRenewalRoute />,
  payroll:         <ExternalCompanyPayroll />,
  incentive:       <ExternalCompanyIncentive />,
  call:            <ExternalCompanyTmCalls />,
  scores:          <ExtScoreRoute />,
  marketer1on1:    <ExtMarketerConsultantRoute />,
  terminal:        <TerminalManagement />,
  contract:        <ExtContractRoute />,
  promotional:     <ExtPromotionalRoute />,
  meetingMap:      <ExtMeetingMapRoute />,
  contractCancel:  <ExtContractCancelRoute />,
  revenue:         <ExtRevenueRoute />,
  settlement:      <ExtSettlementRoute />,
  payout:          <ExtPayoutRoute />,
};

// 표와 화면 맵이 어긋나면 바로 알린다. 테스트(companyFeatures.test.js)가 실패로 잡고,
// 개발 중에는 콘솔로도 보인다. 운영 화면을 깨뜨리지는 않는다(빠진 주소만 안 생긴다).
export function companyElementMismatch() {
  const keys = COMPANY_MENU_FEATURES.map((f) => f.key);
  const mapped = Object.keys(COMPANY_ELEMENTS);
  return {
    missingElement: keys.filter((k) => !mapped.includes(k)),   // 표엔 있는데 화면이 없다
    orphanElement: mapped.filter((k) => !keys.includes(k)),    // 화면은 있는데 표에 없다
  };
}

// ── 본사는 문이 하나다 (2026-09-11) ──────────────────────────────────────────
//
//   /admin/ext/{본사코드}/... 로 들어오면 본사 주소로 돌려보낸다.
//   이 뒷문은 메뉴에 없지만 열려 있었고, 그리로 들어가면 같은 회사의 데이터가
//   **다른 이름으로** 쌓였다. 실제로 갈렸다 — 미팅링크 급여가 본사 주소로는 'PINE',
//   /admin/ext/LINK/payroll 로는 'LINK' 로 저장돼 서로 안 보였다.
//   게다가 그 문 안쪽은 반쪽이었다(직원 목록은 외부 회사 표를 뒤지다 404).
//
//   돌려보낼 곳이 없는 기능(단말기 ID 관리 — 본사 메뉴 자체가 없다)은 그대로 통과시킨다.
//   본사 코드를 못 정했을 때(외부 회사 목록 미로딩 등)도 그대로 통과시킨다 — 지금까지의 동작.
export function CompanyRouteGuard({ feature, children }) {
  const { code } = useParams();
  const scope = useCompanyScope();
  const to = hqRedirectPath(scope?.hqCompanyCode, code, feature);
  return to ? <Navigate to={to} replace /> : children;
}

// ── 본사 화면이 서버에 보낼 회사값 (2026-09-11 C3 2단계) ─────────────────────
//
//   전에는 'PINE' 이라는 글자를 박아 보냈다. 그 글자는 곳에 따라 "파인그룹이라는 회사" 로도
//   "이 사이트 우리 회사" 로도 읽혀서, 미팅링크에서는 자기 데이터를 못 찾는 화면이 생겼다
//   (마케터 1:1: 화면 0건 / 실제 LINK 배정 3건).
//   이제 장부가 답한 주인 회사를 보낸다 — 미팅링크 LINK / 파인그룹 PINE.
//   파인그룹에서는 답이 'PINE' 이라 보내는 값이 전과 같다.
//
//   못 정했으면(장부가 비었거나 운영 테넌트가 여럿) 'PINE' 으로 되돌아간다 — 지금까지의 동작.
function useHqCompanyCode() {
  const scope = useCompanyScope();
  return {
    code: hqCodeOrDefault(scope?.hqCompanyCode),
    ready: Boolean(scope?.hqReady),
  };
}

const hqLoading = <div style={{ padding: 24, color: 'var(--text-muted)' }}>로딩 중...</div>;

/**
 * 본사 화면을 주인 회사 코드로 그린다. 남은 화면들도 이 틀을 쓴다.
 *   children 은 회사코드를 받아 화면을 돌려주는 함수다.
 *   ⚠ 주인 회사를 알기 전에 그리면 'PINE' 으로 한 번 물었다가 다시 물어 화면이 깜빡인다 — 기다린다.
 */
export function HqRoute({ children }) {
  const { code, ready } = useHqCompanyCode();
  if (!ready) return hqLoading;
  return children(code);
}

// ── 옛 '일일콜현황 관리(통합)'(A, /admin/tm-calls) 문지기 (2026-10-14, 통합 계획 3단계 7번) ────────
//   일일콜 켠 회사가 정확히 1곳인 테넌트만 A 를 쓴다. 2곳 이상이면 같은 내용이 작업 대상 → 통합관리로 옮겨졌다.
//   들어오는 길(주소 직접·옛 대시보드 탭·상황판 위젯 링크·본사로 회사 전환·/admin/ext/{본사}/tm-calls)이 모두 이 주소로
//   모이므로 여기 하나에서 거른다. 보낼 곳: B → 통합 일일콜 메뉴 → 첫 화면(callAllRouteDecision, 사용자 결정 4-a).
export function CallAllRoute({ children }) {
  const scope = useCompanyScope();
  const d = callAllRouteDecision({
    meReady: Boolean(scope?.hqReady),
    callAllVisible: Boolean(scope?.callAllVisible),
    canOpenHq: Boolean(scope?.canOpenCallHq),
    integratedReady: Boolean(scope?.integratedReady),
    integratedMenus: scope?.integratedMenus,
  });
  if (d.wait) return hqLoading;
  if (d.to) return <Navigate to={d.to} replace />;
  return children;
}
